import React from 'react';
import styled from 'styled-components';
import { Card as BaseCard } from '../../../../styles/components';
import { Button, OutlineButton } from '../../../../styles/components/common/buttons';
import theme from '../../../../styles/theme';

const Card = styled(BaseCard)`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0;
  background: rgba(15, 23, 42, 0.6);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      120deg,
      transparent, 
      transparent,
      rgba(99, 102, 241, 0.05),
      transparent,
      transparent
    );
    background-size: 200% 100%;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
  
  &:hover:before {
    opacity: 1;
  }
`;

const CardImage = styled.div`
  position: relative;
  padding-top: 56.25%; // 16:9 aspect ratio
  background: ${({ theme: propTheme }) => (propTheme || theme).colors.backgroundLight};
  overflow: hidden;
  
  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  ${Card}:hover & img {
    transform: scale(1.05);
  }
`;

const StatusBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.25rem 0.75rem;
  border-radius: ${({ theme: propTheme }) => (propTheme || theme).borderRadius.pill};
  font-size: ${({ theme: propTheme }) => (propTheme || theme).typography.fontSize.sm};
  font-weight: ${({ theme: propTheme }) => (propTheme || theme).typography.fontWeight.medium};
  background: ${({ theme: propTheme, status }) => {
    const currTheme = propTheme || theme;
    switch (status) {
      case 'live':
      case 'in-progress':
        return currTheme.colors.gradientPrimary;
      case 'upcoming':
        return currTheme.colors.gradientWarning;
      case 'pre-recorded':
        return currTheme.colors.gradientSuccess;
      default:
        return currTheme.colors.gradientPrimary;
    }
  }};
  backdrop-filter: blur(8px);
  color: ${({ theme: propTheme }) => (propTheme || theme).colors.textOnPrimary};
  box-shadow: ${({ theme: propTheme }) => (propTheme || theme).shadows.sm};
  z-index: 1;
  transition: all 0.3s ease;
  
  ${Card}:hover & {
    transform: translateY(-2px);
    box-shadow: ${({ theme: propTheme }) => (propTheme || theme).shadows.md};
  }
`;

const CardContent = styled.div`
  padding: ${({ theme: propTheme }) => (propTheme || theme).spacing.lg};
  display: flex;
  flex-direction: column;
  flex: 1;
  background: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(5px);
`;

const Title = styled.h3`
  margin: 0 0 ${({ theme: propTheme }) => (propTheme || theme).spacing.sm};
  font-size: ${({ theme: propTheme }) => (propTheme || theme).typography.fontSize.lg};
  color: ${({ theme: propTheme }) => (propTheme || theme).colors.textOnDark};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-weight: ${({ theme: propTheme }) => (propTheme || theme).typography.fontWeight.semibold};
  transition: color 0.3s ease;
  
  ${Card}:hover & {
    color: ${({ theme: propTheme }) => (propTheme || theme).colors.primaryLight};
  }
`;

const Info = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme: propTheme }) => (propTheme || theme).spacing.sm};
  font-size: ${({ theme: propTheme }) => (propTheme || theme).typography.fontSize.sm};
  color: ${({ theme: propTheme }) => (propTheme || theme).colors.textOnDark};
  margin-bottom: ${({ theme: propTheme }) => (propTheme || theme).spacing.sm};
  opacity: 0.8;
  
  span {
    display: flex;
    align-items: center;
  }
  
  svg {
    margin-right: 0.25rem;
    font-size: 0.875rem;
  }
`;

const Description = styled.p`
  color: ${({ theme: propTheme }) => (propTheme || theme).colors.textOnDark};
  font-size: ${({ theme: propTheme }) => (propTheme || theme).typography.fontSize.sm};
  margin-bottom: ${({ theme: propTheme }) => (propTheme || theme).spacing.md};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: ${({ theme: propTheme }) => (propTheme || theme).typography.lineHeight.normal};
  opacity: 0.8;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: ${({ theme: propTheme }) => (propTheme || theme).spacing.sm};
  margin-top: ${({ theme: propTheme }) => (propTheme || theme).spacing.md};
`;

const ActionButton = styled(Button)`
  flex: 1;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ViewDetailsButton = styled(OutlineButton)`
  flex: 1;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const CapacityBar = styled.div`
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${({ theme: propTheme }) => (propTheme || theme).borderRadius.sm};
  overflow: hidden;
  margin-bottom: ${({ theme: propTheme }) => (propTheme || theme).spacing.sm};
`;

const CapacityFill = styled.div`
  height: 100%;
  width: ${({ current, max }) => (current / max) * 100}%;
  background: ${({ isFull, theme: propTheme }) => (isFull ? (propTheme || theme).colors.error : (propTheme || theme).colors.success)};
  transition: width 0.3s ease;
`;

const CapacityText = styled.div`
  font-size: ${({ theme: propTheme }) => (propTheme || theme).typography.fontSize.xs};
  color: ${({ isFull, theme: propTheme }) => (isFull ? (propTheme || theme).colors.error : (propTheme || theme).colors.textSecondary)};
  font-weight: ${({ theme: propTheme }) => (propTheme || theme).typography.fontWeight.medium};
`;

const PriceTag = styled.div`
  margin-bottom: 0.5rem;
  color: ${({ isFree, theme: propTheme }) => isFree ? 'rgba(16, 185, 129, 0.9)' : 'rgba(255, 255, 255, 0.9)'};
  font-weight: 500;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.25rem;
  }
`;

const RatingContainer = styled.div`
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: #FFC107;
`;

const WorkshopCard = ({ workshop = {}, onClick, onViewDetails, isRegistered, isCompleted }) => {
  // Ensure workshop is properly initialized with default values
  if (!workshop) workshop = {};
  
  const handleViewDetails = (e) => {
    e.stopPropagation();
    onViewDetails(workshop);
  };

  const getWorkshopStatus = () => {
    if (!workshop) return 'unknown';
    
    // Check if the workshop is completed based on status property
    if (workshop.status === 'completed') return 'completed';
    
    // Otherwise, determine status by type
    if (workshop.type === 'live') return 'live';
    if (isRegistered) return 'registered';
    return workshop.type || 'unknown';
  };

  const getActionButton = () => {
    // Get workshop status but don't assign to a variable since it's not used
    getWorkshopStatus();
    
    // Always show Join Now for live workshops
    if (workshop.type === 'live') {
      return {
        show: true,
        label: 'Join Now',
        action: 'join'
      };
    }

    // Handle upcoming workshops
    if (workshop.type === 'upcoming') {
      if (isRegistered) {
        return {
          show: true,
          label: 'Unregister',
          action: 'unregister'
        };
      }
      return {
        show: true,
        label: 'Register',
        action: 'register',
        disabled: (workshop.currentAttendees || 0) >= (workshop.maxAttendees || 0)
      };
    }

    // Handle pre-recorded workshops
    if (workshop.type === 'pre-recorded') {
      return {
        show: true,
        label: 'Watch Now',
        action: 'watch'
      };
    }

    return {
      show: false
    };
  };

  const status = getWorkshopStatus();
  const actionButton = getActionButton();

  return (
    <Card onClick={() => onClick(workshop)}>
      <CardImage>
        <img 
          src={workshop.thumbnailUrl || 'https://via.placeholder.com/300x200?text=Workshop'} 
          alt={workshop.title || 'Workshop'}
        />
        <StatusBadge status={status}>
          {workshop.type === 'live' ? 'Live' :
           workshop.type === 'upcoming' ? 'Upcoming' :
           workshop.type === 'pre-recorded' ? 'Pre-recorded' :
           status.charAt(0).toUpperCase() + status.slice(1)}
        </StatusBadge>
      </CardImage>
      <CardContent>
        <Title>{workshop.title}</Title>
        <Info>
          <span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            {workshop.date ? new Date(workshop.date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            }) : 'Date TBA'}
          </span>
          <span>•</span>
          <span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            {workshop.time || 'Time TBA'}
          </span>
        </Info>
        <Info>
          <span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            {workshop.presenter || 'TBA'}
          </span>
        </Info>
        
        {/* Always show description, even for completed workshops */}
        <Description>{workshop.description || 'Workshop details will be provided soon.'}</Description>
        
        <div style={{ marginTop: 'auto' }}>
          {/* Always show price, even for completed workshops */}
          {workshop.price !== undefined && (
            <PriceTag isFree={workshop.price === 0}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
              {workshop.price === 0 ? 'Free' : `$${workshop.price}`}
            </PriceTag>
          )}
          
          {/* Show capacity for all workshops with maxAttendees defined */}
          {workshop.maxAttendees && (
            <>
              <CapacityBar>
                <CapacityFill 
                  current={workshop.currentAttendees || 0}
                  max={workshop.maxAttendees || 1}
                  isFull={(workshop.currentAttendees || 0) >= (workshop.maxAttendees || 1)}
                />
              </CapacityBar>
              <CapacityText isFull={(workshop.currentAttendees || 0) >= (workshop.maxAttendees || 1)}>
                {(workshop.currentAttendees || 0) >= (workshop.maxAttendees || 1) ? 'Workshop Full' : 
                 `${workshop.currentAttendees || 0} / ${workshop.maxAttendees || 0} registered`}
              </CapacityText>
            </>
          )}
          
          {(isCompleted || (workshop.rating && workshop.rating > 0)) && (
            <RatingContainer>
              <span>{'★'.repeat(Math.round(workshop.rating || 0))}</span>
              {!isCompleted && (
                <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  ({workshop.reviews?.length || 0})
                </span>
              )}
            </RatingContainer>
          )}
          
          <ButtonsContainer>
            <ViewDetailsButton onClick={handleViewDetails}>
              View Details
            </ViewDetailsButton>
            {actionButton.show && (
              <ActionButton 
                onClick={(e) => {
                  e.stopPropagation();
                  onClick(workshop);
                }}
                disabled={actionButton.disabled}
              >
                {actionButton.label}
              </ActionButton>
            )}
          </ButtonsContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default WorkshopCard;
